import { Separator } from '@/components/_shadcn-ui/separator';
import Input from '@/components/ui/input';
import NewFileUpload from '@/features/_shared/components/file-handler/new-file-upload';
import { Calendar, MessagesSquare, Tags, Rocket, Grid2x2X } from 'lucide-react';
import { memo } from 'react';
import TimeFrameModal from '@/components/ui/timeframe-modal';
import { formatDateRange } from '@/features/ticketings/tickets/ticket/utils/ticket.utils';
import type { UseCreateTicketModalReturn } from './use-create-ticket-modal';
import type { TCreateTicketValidation } from '../../../api/create-ticket.api';
import type { ICreateTicketModalProps } from './create-ticket-modal';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/_r/react-hook-form/base-form';
import Autocomplete from '@/components/ui/_r/inputs/autocomplete';
import MessageError from '@/components/errors/message-error';
import MultiselectAutoComplete from '@/components/ui/_r/inputs/multiselect-autocomplete';
import { CaseOption, SelectedCaseItem } from '@/features/ticketing/tickets/components/modals/_shared/case-option';
import {
  MemberOption,
  SelectedMemberItem,
} from '@/features/ticketing/tickets/components/modals/_shared/ticket-member-option';
import {
  LabelOption,
  SelectedLabelItem,
} from '@/features/ticketing/tickets/components/modals/_shared/label-ticket-option';
import LoadingSpinner2 from '@/components/ui/new-loading-spinner';
import { AutoExpandTextarea } from '@/components/ui/_refactor/form/textareas/autoexpand-textarea';

type MobileCreateTicketFormProps = ICreateTicketModalProps & UseCreateTicketModalReturn;
const MobileCreateTicketForm = memo((props: MobileCreateTicketFormProps) => {
  const {
    // Form
    triggerFrom,
    form,
    handleSubmitForm,
    isLoadingCreateTicket,

    // Date modal
    dateValues,
    isOpenDateModal,
    handleOpenDateModal,
    handleCloseDateModal,
    handleSetDateValuesModal,

    // Spaces
    listSpacesOptions,
    isLoadingListSpacesData,

    // Space columns
    listSpaceColumnsOptions,
    isLoadingListSpaceColumnsData,

    // Space members
    listTicketMemberOptions,
    isLoadingListTicketMemberData,

    // Cases
    listCaseOptions,
    isLoadingListCaseData,

    // Label tickets
    listLabelTicketOptions,
    isLoadingListLabelTicketData,

    // Files
    extractedAttachments,
    uploadingFileIds,
    isUploadingFiles,
    handleAddFiles,
    handleRemoveFile,
  } = props;

  // RENDER
  return (
    <div className="pt-[56px] pb-[16px]">
      <div className="flex items-center justify-between px-[16px]">
        <div className="flex flex-col gap-[8px]">
          <p className="text-[16px] font-[700] text-[#0C8DBF]">Create New Ticket</p>
          <p className="text-[12px] font-[400] text-[#9D9D9C]">
            Add a new ticket for your team. Set the title, date, member, and other details to keep work organized.
          </p>
        </div>
      </div>
      <Separator className="mt-[10px]" />

      <div className="mt-[12px] max-h-[60vh] overflow-y-auto">
        <Form {...form}>
          <form
            id="add-ticketing-space-column-form"
            className="h-fit"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit(handleSubmitForm as (values: TCreateTicketValidation) => void)(e);
            }}
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => {
                const error = form?.formState?.errors?.title?.message || '';
                const value = field?.value || '';

                return (
                  <FormItem>
                    <div className="px-[16px]">
                      <FormControl>
                        <AutoExpandTextarea
                          {...field}
                          value={value}
                          placeholder="Enter a title for this card..."
                          className="text-[#25151E] text-[24px] font-[700] w-full bg-transparent border-0 border-b-[2px] border-transparent focus:border-[#0C8DBF] focus:outline-none focus:ring-0 px-0 py-[0px] transition-colors duration-200 placeholder:text-[#9D9D9C] placeholder:font-[400] resize-none overflow-hidden rounded-none min-h-0 leading-tight p-0"
                        />
                      </FormControl>
                      <MessageError message={error} />
                    </div>
                  </FormItem>
                );
              }}
            />

            <div className="mt-[20px] space-y-[10px] px-[16px]">
              <FormField
                control={form.control}
                name="spaceId"
                render={({ field }) => {
                  const disabled = triggerFrom !== 'case' ? true : isLoadingListSpacesData;
                  const error = form?.formState?.errors?.spaceId?.message || '';
                  const value = listSpacesOptions?.find((option) => option.spaceId === field.value) || null;

                  return (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="flex items-center gap-[8px]">
                          <Rocket className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                          <span className="text-[12px] font-[700] text-[#4B4B4B]">Space</span>
                        </FormLabel>
                        <FormControl>
                          <div className="max-w-[220px] w-full">
                            <Autocomplete
                              labelKey="name"
                              valueKey="spaceId"
                              placeholder="Select spaces"
                              disabled={disabled}
                              value={value}
                              options={listSpacesOptions || []}
                              isLoading={isLoadingListSpacesData}
                              onChange={(selectedValue) => {
                                field.onChange(selectedValue?.spaceId || '');
                              }}
                              onClear={() => {
                                form.setValue('spaceId', '', { shouldValidate: true });
                                form.setValue('spaceColumnId', '', { shouldValidate: true });
                              }}
                            />
                          </div>
                        </FormControl>
                      </div>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="spaceColumnId"
                render={({ field }) => {
                  const disabled =
                    triggerFrom !== 'case' ? true : !form.watch('spaceId') || isLoadingListSpaceColumnsData;
                  const error = form?.formState?.errors?.spaceColumnId?.message || '';
                  const value = listSpaceColumnsOptions?.find((option) => option.spaceColumnId === field.value) || null;

                  return (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="flex items-center gap-[8px]">
                          <Grid2x2X className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                          <span className="text-[12px] font-[700] text-[#4B4B4B]">Column</span>
                        </FormLabel>
                        <FormControl>
                          <div className="max-w-[220px] w-full">
                            <Autocomplete
                              labelKey="name"
                              valueKey="spaceColumnId"
                              placeholder="Select columns"
                              value={value}
                              disabled={disabled}
                              isLoading={isLoadingListSpaceColumnsData}
                              options={listSpaceColumnsOptions || []}
                              onChange={(selectedValue) => {
                                field.onChange(selectedValue?.spaceColumnId ?? '');
                              }}
                              onClear={() => {
                                form.setValue('spaceColumnId', '', { shouldValidate: true });
                              }}
                            />
                          </div>
                        </FormControl>
                      </div>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="startDate"
                render={({ field: _field }) => {
                  const error =
                    form?.formState?.errors?.startDate?.message || form?.formState?.errors?.endDate?.message || '';

                  return (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="flex items-center gap-[8px]">
                          <Calendar className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                          <span className="text-[12px] font-[700] text-[#4B4B4B]">Dates</span>
                        </FormLabel>
                        <div className="max-w-[220px] w-full">
                          <FormControl>
                            <Input
                              placeholder="Set Due date"
                              value={formatDateRange(dateValues)}
                              type="text"
                              readOnly
                              className="cursor-pointer"
                              onClick={() => handleOpenDateModal()}
                            />
                          </FormControl>
                        </div>
                      </div>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="caseIds"
                render={({ field }) => {
                  const error = form?.formState?.errors?.caseIds?.message || '';
                  const value = listCaseOptions?.filter((option) => field.value?.includes(option.caseId)) || [];
                  const disabled = isLoadingListCaseData;

                  return (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="flex items-center gap-[8px]">
                          <MessagesSquare className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                          <span className="text-[12px] font-[700] text-[#4B4B4B]">Case</span>
                        </FormLabel>
                        <div className="max-w-[220px] w-full">
                          <FormControl>
                            <MultiselectAutoComplete
                              labelKey="name"
                              valueKey="caseId"
                              placeholder="Select case"
                              searchPlaceholder="Search case"
                              options={listCaseOptions || []}
                              value={value}
                              disabled={disabled}
                              isLoading={isLoadingListCaseData}
                              onChange={(value) => {
                                field.onChange(value.map((option) => option.caseId));
                              }}
                              onClearAll={() => {
                                field.onChange([]);
                              }}
                              renderOption={(option) => <CaseOption option={option} />}
                              renderSelectedItem={(option) => (
                                <SelectedCaseItem
                                  option={option}
                                  onRemove={(option) => {
                                    if (option) {
                                      field.onChange(field.value?.filter((id: string) => id !== option.caseId) || []);
                                    }
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                        </div>
                      </div>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="userIds"
                render={({ field }) => {
                  const error = form?.formState?.errors?.userIds?.message || '';
                  const value = listTicketMemberOptions?.filter((option) => field.value?.includes(option.userId)) || [];
                  const disabled = isLoadingListTicketMemberData;

                  return (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="flex items-center gap-[8px]">
                          <Calendar className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                          <span className="text-[12px] font-[700] text-[#4B4B4B]">Member</span>
                        </FormLabel>
                        <div className="max-w-[220px] w-full">
                          <FormControl>
                            <MultiselectAutoComplete
                              labelKey="fullname"
                              valueKey="userId"
                              placeholder="Select members"
                              searchPlaceholder="Search members"
                              disabled={disabled}
                              isLoading={isLoadingListTicketMemberData}
                              options={listTicketMemberOptions || []}
                              value={value}
                              onChange={(value) => {
                                field.onChange(value.map((option) => option.userId));
                              }}
                              onClearAll={() => {
                                field.onChange([]);
                              }}
                              renderOption={(option) => <MemberOption option={option} />}
                              renderSelectedItem={(option) => (
                                <SelectedMemberItem
                                  option={option}
                                  onRemove={(option) => {
                                    if (option) {
                                      field.onChange(field.value?.filter((id: string) => id !== option.userId) || []);
                                    }
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                        </div>
                      </div>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="labelTicketIds"
                render={({ field }) => {
                  const error = form?.formState?.errors?.labelTicketIds?.message || '';
                  const value =
                    listLabelTicketOptions?.filter((option) => field.value?.includes(option.labelTicketId)) || [];
                  const disabled = isLoadingListLabelTicketData;

                  return (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="flex items-center gap-[8px]">
                          <Tags className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                          <span className="text-[12px] font-[700] text-[#4B4B4B]">Label</span>
                        </FormLabel>
                        <div className="max-w-[220px] w-full">
                          <FormControl>
                            <MultiselectAutoComplete
                              labelKey="name"
                              valueKey="labelTicketId"
                              placeholder="Select labels"
                              searchPlaceholder="Search labels"
                              disabled={disabled}
                              isLoading={isLoadingListLabelTicketData}
                              options={listLabelTicketOptions || []}
                              value={value}
                              onChange={(value) => {
                                field.onChange(value.map((option) => option.labelTicketId));
                              }}
                              onClearAll={() => {
                                field.onChange([]);
                              }}
                              renderOption={(option) => <LabelOption option={option} />}
                              renderSelectedItem={(option) => (
                                <SelectedLabelItem
                                  option={option}
                                  onRemove={(option) => {
                                    if (option) {
                                      field.onChange(
                                        field.value?.filter((id: string) => id !== option.labelTicketId) || []
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                        </div>
                      </div>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => {
                  const error = form?.formState?.errors?.content?.message || '';
                  const value = field?.value || '';

                  return (
                    <FormItem>
                      <div className="space-y-[8px]">
                        <FormLabel className="flex items-center gap-[8px]">
                          <span className="text-[12px] font-[700] text-[#4B4B4B]">Content</span>{' '}
                          <span className="text-[12px] font-[400] text-[#9D9D9C]">(Optional)</span>
                        </FormLabel>
                        <FormControl>
                          <AutoExpandTextarea
                            {...field}
                            value={value}
                            placeholder="Enter ticket details..."
                            minHeight={100}
                            className="w-full rounded-[8px] border border-[#D9D9D9] p-[8px] text-[13px]"
                          />
                        </FormControl>
                      </div>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="attachments"
                render={({ field: _field }) => {
                  const error = form?.formState?.errors?.attachments?.message || '';

                  return (
                    <FormItem>
                      <div className="space-y-[8px]">
                        <FormLabel className="flex items-center gap-[8px]">
                          <span className="text-[12px] font-[700] text-[#4B4B4B]">Attachments</span>{' '}
                          <span className="text-[12px] font-[400] text-[#9D9D9C]">(Optional)</span>
                        </FormLabel>
                        <FormControl>
                          <NewFileUpload
                            accept="*"
                            maxSize={20 * 1024 * 1024} // 20MB
                            maxFiles={10}
                            initialFiles={extractedAttachments || []}
                            isUploadingFiles={isUploadingFiles}
                            uploadingFileIds={uploadingFileIds}
                            onFilesAdded={(files) => handleAddFiles(files)}
                            onFileRemoved={(removedFile) => handleRemoveFile(removedFile)}
                          />
                        </FormControl>
                      </div>
                      <MessageError message={error} />
                    </FormItem>
                  );
                }}
              />
            </div>
            <div className="flex justify-end px-[16px] mt-[10px]">
              <button
                disabled={isLoadingCreateTicket || isUploadingFiles}
                form="add-ticketing-space-column-form"
                type="submit"
                className="w-fit bg-[#0C8DBF] cursor-pointer disabled:cursor-not-allowed disabled:bg-[#9D9D9C] text-[14px] text-[#FFF] font-[700] rounded-[8px] py-[8px] px-[16px] flex items-center gap-[4px]"
              >
                {isLoadingCreateTicket ? <LoadingSpinner2 /> : null}
                Save Changes
              </button>
            </div>
          </form>
        </Form>
        <TimeFrameModal
          submitText="Apply Date"
          title="Set Due date"
          initialDateRange={dateValues ?? undefined}
          isOpen={isOpenDateModal}
          onClose={() => {
            handleCloseDateModal();
          }}
          onApply={(dateRange) => {
            handleSetDateValuesModal(dateRange);
            handleCloseDateModal();
            form.setValue('startDate', dateRange?.from?.toISOString());
            form.setValue('endDate', dateRange?.to?.toISOString());
          }}
        />
      </div>
    </div>
  );
});
MobileCreateTicketForm.displayName = 'MobileCreateTicketForm';
export default MobileCreateTicketForm;
